// Ficha de cliente del venue (FRA-354). Hasta hoy el cliente no existía:
// eran campos sueltos recapturados en cada evento (nombre, apellido,
// teléfono, correo, persona física o moral, empresa, RFC) y en cada lead.
// Aquí se vuelve una entidad propia por venue —venues/{id}/clientes— con
// su historial: qué eventos ha hecho, cuánto valen, cuánto ha pagado y
// cuánto debe.
//
// Lectura tolerante: los eventos existentes conservan sus campos de cliente
// y siguen funcionando sin ficha; el enlace es `clienteId` en el evento (y
// en el lead) y se puede poner después. Nada se migra.
//
// OJO con el nombre: `users` con role "client" son cuentas del homesite y
// no tienen relación con esto. El cliente de aquí es el del venue.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { getValorEvento, totalDesglose } from "./valorEvento";
import { costosDeEvento, costosPorEvento, esComisionPlanner } from "./costos";

export const TIPOS_CLIENTE = [
  { value: "fisica", label: "Persona física" },
  { value: "moral", label: "Persona moral" },
];

const trim = (v) => String(v ?? "").trim();

export const normalizaCorreo = (v) => trim(v).toLowerCase();

// Los últimos 10 dígitos: "+52 55 1234 5678", "5512345678" y "55-1234-5678"
// son el mismo teléfono.
export const normalizaTelefono = (v) => {
  const digitos = String(v ?? "").replace(/\D/g, "");
  return digitos.length > 10 ? digitos.slice(-10) : digitos;
};

// Forma canónica de la ficha. Todo opcional menos "algo con qué nombrarlo".
export function limpiaDatosCliente(datos = {}) {
  return {
    nombre: trim(datos.nombre),
    apellido: trim(datos.apellido),
    clientType: datos.clientType === "moral" ? "moral" : "fisica",
    nombreEmpresa: trim(datos.nombreEmpresa),
    correo: normalizaCorreo(datos.correo),
    telefono: trim(datos.telefono),
    rfc: trim(datos.rfc).toUpperCase(),
    notas: trim(datos.notas),
    // Varios contactos por ficha. Se normaliza aquí para que TODA escritura
    // pase por el mismo filtro; si no viene, la ficha se queda como estaba
    // (un solo contacto, el inline) y nadie tiene que migrar nada.
    // Un array vacío NO borra los campos inline: sería perder el nombre de la
    // ficha por pasar una lista que todavía no se llena.
    ...(Array.isArray(datos.contactos) && datos.contactos.length > 0
      ? patchContactos(datos.contactos)
      : {}),
  };
}

export const tieneIdentidad = (c) =>
  !!(trim(c?.nombre) || trim(c?.nombreEmpresa) || trim(c?.correo) || trim(c?.telefono));

// "Empresa S.A." para persona moral; "Nombre Apellido" para física; y si no
// hay nada, el correo o el teléfono antes que "Cliente".
export function nombreCliente(c) {
  if (!c) return "Cliente";
  if (c.clientType === "moral" && trim(c.nombreEmpresa)) return trim(c.nombreEmpresa);
  const persona = [trim(c.nombre), trim(c.apellido)].filter(Boolean).join(" ");
  return persona || trim(c.nombreEmpresa) || trim(c.correo) || trim(c.telefono) || "Cliente";
}

// El contacto de una persona moral (quién llama), si lo hay.
export function contactoCliente(c) {
  if (!c || c.clientType !== "moral" || !trim(c.nombreEmpresa)) return "";
  return [trim(c.nombre), trim(c.apellido)].filter(Boolean).join(" ");
}

// Del evento a la ficha: los campos que hoy viven regados en cada evento.
export function clienteDesdeEvento(ev = {}) {
  return limpiaDatosCliente({
    nombre: ev.cliente,
    apellido: ev.apellido,
    clientType: ev.clientType,
    nombreEmpresa: ev.nombreEmpresa,
    correo: ev.correo,
    telefono: ev.telefono,
    rfc: ev.rfc,
  });
}

// Del lead a la ficha. El lead trae un solo "name"; se parte en nombre y
// apellido por el primer espacio, que es lo más cercano a la verdad sin
// preguntar.
export function clienteDesdeLead(lead = {}) {
  const partes = trim(lead.name).split(/\s+/).filter(Boolean);
  return limpiaDatosCliente({
    nombre: partes[0] || "",
    apellido: partes.slice(1).join(" "),
    correo: lead.userEmail,
    telefono: lead.phone,
  });
}

// De la ficha al evento: los campos con los nombres que el evento ya usa,
// más el enlace. Al elegir un cliente existente "los campos se llenan solos".
export function camposEventoDesdeCliente(c) {
  if (!c) return {};
  return {
    clienteId: c.id || null,
    cliente: c.nombre || "",
    apellido: c.apellido || "",
    clientType: c.clientType === "moral" ? "moral" : "fisica",
    nombreEmpresa: c.nombreEmpresa || "",
    correo: c.correo || "",
    telefono: c.telefono || "",
    rfc: c.rfc || "",
  };
}

// De la ficha al lead: para "generar un lead desde un cliente que ya
// existe" (el cliente recurrente que pregunta por otra fecha).
export function camposLeadDesdeCliente(c) {
  if (!c) return {};
  return {
    clienteId: c.id || null,
    name: nombreCliente(c),
    userEmail: c.correo || "",
    phone: c.telefono || "",
  };
}

// ── Contactos (varios por ficha) ─────────────────────────────────────
//
// Una ficha nació con UN contacto inline (nombre/apellido/correo/telefono) y
// eso alcanzaba para una persona física. No alcanza para una empresa: en
// "ClassEvents" hablas con Christopher y con Valeria, y los dos correos son
// buenos. El asistente del directorio lo delataba preguntando "aparece con
// dos correos, elige cuál se queda" — una pregunta mal planteada, porque no
// sobra ninguno.
//
// SIN MIGRACIÓN. `contactos` es opcional y los campos inline se conservan
// como espejo del contacto PRINCIPAL, así que todo lo que ya los lee
// —ClientePicker, camposEventoDesdeCliente, buscarCoincidencia, el nombre de
// la ficha— sigue funcionando sin cambios. Una ficha vieja simplemente
// tiene un contacto, derivado al vuelo.
//
// Lo que este modelo NO intenta resolver: la agencia con la que coordinas
// todo el evento. Eso ya lo atiende el planner con su comisión (FRA-355), y
// meterlo aquí volvería la ficha de cliente un directorio de terceros.

export const nuevoIdContacto = () =>
  `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function limpiaContacto(c = {}) {
  return {
    id: c.id || nuevoIdContacto(),
    nombre: trim(c.nombre),
    apellido: trim(c.apellido),
    puesto: trim(c.puesto),
    correo: normalizaCorreo(c.correo),
    telefono: trim(c.telefono),
    principal: c.principal === true,
  };
}

export const nombreContacto = (c) =>
  [trim(c?.nombre), trim(c?.apellido)].filter(Boolean).join(" ") ||
  trim(c?.correo) ||
  trim(c?.telefono) ||
  "Contacto";

// Los contactos de una ficha, siempre al menos uno. Si la ficha no tiene
// `contactos`, se deriva del inline — por eso las fichas viejas se ven igual
// que las nuevas sin haber tocado un solo documento.
export function contactosDeCliente(c) {
  const lista = Array.isArray(c?.contactos) ? c.contactos.filter(Boolean) : [];
  if (lista.length > 0) {
    // Exactamente un principal: si ninguno lo dice, manda el primero.
    const conPrincipal = lista.some((x) => x.principal === true);
    return lista.map((x, i) => ({
      ...limpiaContacto(x),
      principal: conPrincipal ? x.principal === true : i === 0,
    }));
  }
  if (!c) return [];
  const derivado = limpiaContacto({
    id: "principal",
    nombre: c.nombre,
    apellido: c.apellido,
    correo: c.correo,
    telefono: c.telefono,
    principal: true,
  });
  // Una ficha sin nada con qué nombrar al contacto no inventa uno vacío.
  return nombreContacto(derivado) === "Contacto" ? [] : [derivado];
}

export const contactoPrincipal = (c) =>
  contactosDeCliente(c).find((x) => x.principal) || null;

// Todos los correos y teléfonos de la ficha, para que buscar por cualquiera
// de ellos encuentre al cliente.
export const correosDeCliente = (c) =>
  [...new Set(contactosDeCliente(c).map((x) => x.correo).filter(Boolean))];
export const telefonosDeCliente = (c) =>
  [...new Set(contactosDeCliente(c).map((x) => x.telefono).filter(Boolean))];

// El patch que deja la ficha consistente: la lista con UN principal, y los
// campos inline como su espejo. Escribir siempre por aquí es lo que evita
// que las dos representaciones se separen.
export function patchContactos(contactos, { principalId = null } = {}) {
  const lista = (Array.isArray(contactos) ? contactos : [])
    .map(limpiaContacto)
    .filter((c) => nombreContacto(c) !== "Contacto");
  if (lista.length === 0) {
    return { contactos: [], nombre: "", apellido: "", correo: "", telefono: "" };
  }
  const idx = principalId ? lista.findIndex((c) => c.id === principalId) : -1;
  const principal = idx >= 0 ? idx : Math.max(0, lista.findIndex((c) => c.principal));
  const conBandera = lista.map((c, i) => ({ ...c, principal: i === principal }));
  const p = conBandera[principal];
  return {
    contactos: conBandera,
    nombre: p.nombre,
    apellido: p.apellido,
    correo: p.correo,
    telefono: p.telefono,
  };
}

// Busca una ficha que sea la misma persona: correo exacto primero (es lo
// más confiable), teléfono después. Devuelve null si no hay coincidencia.
export function buscarCoincidencia(clientes = [], { correo, telefono } = {}) {
  const lista = (clientes || []).filter((c) => c && !c.archivedAt);
  // Busca en TODOS los contactos, no solo en el principal: si escribes el
  // correo de Valeria, el cliente es ClassEvents aunque la ficha lleve el de
  // Christopher arriba. En fichas de un solo contacto es idéntico a antes.
  const mail = normalizaCorreo(correo);
  if (mail) {
    const porCorreo = lista.find((c) =>
      correosDeCliente(c).some((x) => normalizaCorreo(x) === mail)
    );
    if (porCorreo) return porCorreo;
  }
  const tel = normalizaTelefono(telefono);
  if (tel.length >= 8) {
    const porTel = lista.find((c) =>
      telefonosDeCliente(c).some((x) => normalizaTelefono(x) === tel)
    );
    if (porTel) return porTel;
  }
  return null;
}

const ms = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const d = new Date(v);
  return isNaN(d) ? 0 : d.getTime();
};

// Todo lo que la ficha necesita en una pasada: sus eventos (los ligados por
// `clienteId`), cuánto valen, cuánto ha pagado y cuánto debe. Los
// cancelados se listan pero no suman valor ni deuda; lo que pagaron sí se
// reporta aparte para que se vea de dónde salió.
//
// Con `costos` además dice cómo salió cada evento —costos y utilidad— y el
// acumulado: es lo que se quiere ver cuando el cliente vuelve a cotizar.
// Los costos de un cancelado se listan (se gastaron de verdad) pero no
// entran al margen: no hubo venta contra la que medirlos.
export function resumenCliente({ cliente, events = [], payments = [], costos = [] }) {
  if (!cliente?.id) {
    return {
      eventos: [], valorTotal: 0, pagado: 0, debe: 0, cancelados: 0, ultimoEvento: null,
      costosTotal: 0, utilidad: 0, margen: null, conResultados: 0,
    };
  }
  const propios = (events || []).filter((e) => e?.clienteId === cliente.id);
  const ids = new Set(propios.map((e) => e.id));
  const pagosPorEvento = {};
  (payments || []).forEach((p) => {
    if (!ids.has(p?.eventId)) return;
    pagosPorEvento[p.eventId] = (pagosPorEvento[p.eventId] || 0) + (Number(p.monto) || 0);
  });
  const costosDeEventos = costosPorEvento(costos);
  let valorTotal = 0;
  let pagado = 0;
  let cancelados = 0;
  let costosTotal = 0;
  let conResultados = 0;
  const eventos = propios
    .map((e) => {
      const cancelado = e.status === "cancelado";
      const valor = cancelado ? 0 : getValorEvento(e);
      const pag = pagosPorEvento[e.id] || 0;
      const cos = costosDeEventos[e.id]?.total || 0;
      if (cancelado) cancelados += 1;
      else {
        costosTotal += cos;
        if (valor > 0 || cos > 0) conResultados += 1;
      }
      valorTotal += valor;
      pagado += pag;
      return {
        eventId: e.id,
        nombre: e.nombre || "Sin nombre",
        startTime: e.startTime,
        cancelado,
        valor,
        pagado: pag,
        saldo: cancelado ? 0 : Math.max(0, valor - pag),
        costos: cos,
        // null en cancelados: no hay venta contra la que medir el gasto.
        utilidad: cancelado ? null : valor - cos,
        margen: !cancelado && valor > 0 ? (valor - cos) / valor : null,
      };
    })
    .sort((a, b) => ms(b.startTime) - ms(a.startTime));
  const utilidad = valorTotal - costosTotal;
  return {
    eventos,
    valorTotal,
    pagado,
    // Lo que debe es la suma de saldos vivos, no valor − pagado: lo que
    // pagó un evento cancelado no abona a los demás.
    debe: eventos.reduce((s, e) => s + e.saldo, 0),
    cancelados,
    ultimoEvento: eventos[0] || null,
    costosTotal,
    utilidad,
    margen: valorTotal > 0 ? utilidad / valorTotal : null,
    conResultados,
  };
}

// Un evento del cliente, abierto: qué se le cobró (los conceptos de la
// cotización), qué costó, quién lo trabajó (proveedores) y quién lo trajo
// (el planner). No lee nada nuevo — todo eso ya vive en el evento y en
// `costos`; aquí se junta para que el historial del cliente se pueda leer
// sin entrar evento por evento.
//
// Tolerante por diseño: un evento viejo sin desglose cae a `precioAcordado`
// (vía getValorEvento) y se marca `sinDesglose`; sin proveedores asignados o
// sin costos devuelve listas vacías, no ceros mentirosos.
export function detalleEventoCliente({ event, costos = [], proveedores = [] }) {
  const desglose = Array.isArray(event?.desglose) ? event.desglose : [];
  const ivaEnabled = event?.ivaEnabled === true;

  const conceptos = desglose.map((l, i) => {
    const units = Number(l?.units) || 0;
    const unitPrice = Number(l?.unitPrice) || 0;
    return {
      id: `l${i}`,
      concepto: trim(l?.concept) || "Concepto",
      units,
      unitPrice,
      importe: units * unitPrice,
    };
  });
  const subtotal = conceptos.reduce((s, l) => s + l.importe, 0);
  const totalCobrado = desglose.length > 0 ? totalDesglose(desglose, ivaEnabled) : 0;
  // El valor manda sobre el total del desglose: es el mismo criterio con el
  // que se calculan saldos en toda la app (cotización si la hay, si no el
  // precio acordado).
  const valor = getValorEvento(event);

  const propios = costosDeEvento(costos, event?.id).slice().sort(
    (a, b) => (Number(b?.monto) || 0) - (Number(a?.monto) || 0)
  );
  const totalCostos = propios.reduce((s, c) => s + (Number(c?.monto) || 0), 0);
  const porPagar = propios
    .filter((c) => (c?.estado || "por_pagar") === "por_pagar")
    .reduce((s, c) => s + (Number(c?.monto) || 0), 0);
  const utilidad = valor - totalCostos;

  // Lo gastado con cada proveedor en ESTE evento, para colgárselo a su
  // renglón sin recorrer los costos una vez por proveedor.
  const gastoPorProveedor = {};
  propios.forEach((c) => {
    if (!c?.proveedorId) return;
    gastoPorProveedor[c.proveedorId] =
      (gastoPorProveedor[c.proveedorId] || 0) + (Number(c?.monto) || 0);
  });

  const plannerId = event?.plannerId || null;
  const ficha = (id) => (proveedores || []).find((p) => p?.id === id) || null;
  const asignados = Array.isArray(event?.proveedoresAsignados)
    ? event.proveedoresAsignados.filter(Boolean)
    : [];

  const listaProveedores = asignados.map((a, i) => ({
    id: `${a?.proveedorId || "sin"}-${a?.servicioId || i}`,
    proveedorId: a?.proveedorId || null,
    nombre: trim(a?.proveedorNombre) || ficha(a?.proveedorId)?.nombre || "Proveedor",
    categoria: a?.categoria || ficha(a?.proveedorId)?.categoria || null,
    servicio: trim(a?.servicioNombre) || "",
    cantidad: Number(a?.cantidad) > 0 ? Number(a.cantidad) : 1,
    costo: gastoPorProveedor[a?.proveedorId] || 0,
    esPlanner: !!plannerId && a?.proveedorId === plannerId,
  }));

  const costoComision = plannerId
    ? propios.find((c) => c?.proveedorId === plannerId && esComisionPlanner(c)) || null
    : null;
  const planner = plannerId
    ? {
        id: plannerId,
        nombre:
          trim(event?.plannerNombre) ||
          ficha(plannerId)?.nombre ||
          listaProveedores.find((p) => p.esPlanner)?.nombre ||
          "Planner",
        comision: costoComision
          ? {
              monto: Number(costoComision.monto) || 0,
              estado: costoComision.estado || "por_pagar",
            }
          : null,
      }
    : null;

  return {
    conceptos,
    ivaEnabled,
    sinDesglose: desglose.length === 0,
    subtotal,
    iva: ivaEnabled ? Math.round((totalCobrado - subtotal) * 100) / 100 : 0,
    totalCobrado,
    valor,
    costos: propios,
    totalCostos,
    porPagar,
    utilidad,
    // null (no 0) sin valor capturado: "no sabemos" y "0%" son cosas
    // distintas, igual que en utils/costos.js.
    margen: valor > 0 ? utilidad / valor : null,
    proveedores: listaProveedores,
    planner,
  };
}

// Eventos viejos (sin `clienteId`) que parecen ser de este cliente por
// correo o teléfono: se ofrecen para ligarlos, no se ligan solos.
export function eventosSinLigarQueCoinciden(cliente, events = []) {
  if (!cliente) return [];
  const mail = normalizaCorreo(cliente.correo);
  const tel = normalizaTelefono(cliente.telefono);
  return (events || []).filter((e) => {
    if (!e || e.clienteId || e.sinFichaCliente || e.eventType !== "event") return false;
    if (mail && normalizaCorreo(e.correo) === mail) return true;
    if (tel.length >= 8 && normalizaTelefono(e.telefono) === tel) return true;
    return false;
  });
}

// ── Escrituras ───────────────────────────────────────────────────────

export async function crearCliente({ venueId, datos, user, origen = "manual" }) {
  if (!venueId || !user?.uid) throw new Error("Sin venue o sin usuario");
  const limpio = limpiaDatosCliente(datos);
  if (!tieneIdentidad(limpio)) throw new Error("El cliente necesita al menos un nombre, correo o teléfono.");
  const ref = await addDoc(collection(db, "venues", venueId, "clientes"), {
    ...limpio,
    origen, // manual | evento | lead
    archivedAt: null,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
  return ref.id;
}

// Crea la ficha o liga la que ya existe (misma persona por correo o
// teléfono). Devuelve { clienteId, creado }.
export async function crearOLigarCliente({ venueId, clientes = [], datos, user, origen = "evento" }) {
  const limpio = limpiaDatosCliente(datos);
  if (!tieneIdentidad(limpio)) return { clienteId: null, creado: false };
  const existente = buscarCoincidencia(clientes, limpio);
  if (existente) return { clienteId: existente.id, creado: false };
  const clienteId = await crearCliente({ venueId, datos: limpio, user, origen });
  return { clienteId, creado: true };
}

export async function actualizarCliente({ venueId, clienteId, datos, user }) {
  if (!venueId || !clienteId || !user?.uid) throw new Error("Sin venue, cliente o usuario");
  const limpio = limpiaDatosCliente(datos);
  if (!tieneIdentidad(limpio)) throw new Error("El cliente necesita al menos un nombre, correo o teléfono.");
  await updateDoc(doc(db, "venues", venueId, "clientes", clienteId), {
    ...limpio,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
}

export async function archivarCliente({ venueId, clienteId, user, archivar = true }) {
  if (!venueId || !clienteId || !user?.uid) return;
  await updateDoc(doc(db, "venues", venueId, "clientes", clienteId), {
    archivedAt: archivar ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
}

// Liga un evento a su ficha (y opcionalmente rellena los campos del evento
// desde la ficha, para los eventos viejos capturados a mano).
//
// `limpiarRol`: el rol es del cliente EN ESTE evento (`clienteRol`), no del
// cliente. Al cambiar de cliente principal se borra; si no, la «Novia»
// anterior quedaba pegada a la productora nueva. Quien llama lo pide cuando
// el cliente cambia (rellenar datos del mismo cliente conserva su rol).
export async function ligarEventoACliente({
  venueId,
  eventId,
  cliente,
  calendarId = "default",
  rellenarCampos = false,
  limpiarRol = false,
}) {
  if (!venueId || !eventId || !cliente?.id) return;
  const ref = doc(db, "venues", venueId, "calendars", calendarId, "events", eventId);
  const patch = rellenarCampos
    ? camposEventoDesdeCliente(cliente)
    : { clienteId: cliente.id };
  if (limpiarRol) patch.clienteRol = "";
  await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
}

export async function desligarEventoDeCliente({ venueId, eventId, calendarId = "default" }) {
  if (!venueId || !eventId) return;
  await updateDoc(doc(db, "venues", venueId, "calendars", calendarId, "events", eventId), {
    clienteId: null,
    clienteRol: "",
    updatedAt: serverTimestamp(),
  });
}

// ── Varios clientes por evento ───────────────────────────────────────
//
// Un evento tiene UNA ficha principal (`clienteId`: la que carga con el
// dinero en su historial) y las demás que participan —el novio y la novia,
// la productora y quien paga— van en `clientesAdicionales: [{clienteId,
// rol}]`. El rol es texto libre, sin catálogo: cada venue nombra a su gente
// como quiera. La principal también lleva su rol en `clienteRol`.
//
// Lectura tolerante: un evento sin estos campos es un evento con su ficha
// principal y nada más, como hasta hoy. Nada se migra.

// Todas las coincidencias por correo o teléfono, no solo la primera: cuando
// hay dos fichas con el mismo correo (un duplicado), quien liga debe verlas
// las dos y elegir, no recibir una al azar.
export function coincidenciasDeContacto(clientes = [], { correo, telefono } = {}) {
  const lista = (clientes || []).filter((c) => c && !c.archivedAt);
  const mail = normalizaCorreo(correo);
  const tel = normalizaTelefono(telefono);
  const porTel = tel.length >= 8;
  if (!mail && !porTel) return [];
  return lista.filter(
    (c) =>
      (mail && correosDeCliente(c).some((x) => normalizaCorreo(x) === mail)) ||
      (porTel && telefonosDeCliente(c).some((x) => normalizaTelefono(x) === tel))
  );
}

// Los adicionales del evento, limpios: solo entradas con clienteId y sin
// repetir la principal.
export function clientesAdicionalesDe(ev = {}) {
  const principal = ev?.clienteId || null;
  const vistos = new Set();
  return (Array.isArray(ev?.clientesAdicionales) ? ev.clientesAdicionales : [])
    .filter((x) => x && typeof x.clienteId === "string" && x.clienteId)
    .filter((x) => x.clienteId !== principal)
    .filter((x) => (vistos.has(x.clienteId) ? false : (vistos.add(x.clienteId), true)))
    .map((x) => ({ clienteId: x.clienteId, rol: trim(x.rol) }));
}

// Todas las fichas del evento, la principal primero y marcada.
export function clientesDelEvento(ev = {}) {
  const lista = [];
  if (ev?.clienteId) lista.push({ clienteId: ev.clienteId, rol: trim(ev.clienteRol), principal: true });
  clientesAdicionalesDe(ev).forEach((x) => lista.push({ ...x, principal: false }));
  return lista;
}

// Eventos donde esta ficha participa SIN ser la principal, con su rol. Van
// al historial de la ficha como participación, no como venta: el dinero de
// un evento se cuenta una sola vez, en la ficha principal.
export function eventosDondeParticipa(cliente, events = []) {
  if (!cliente?.id) return [];
  return (events || [])
    .map((e) => {
      const entrada = clientesAdicionalesDe(e).find((x) => x.clienteId === cliente.id);
      return entrada ? { event: e, rol: entrada.rol } : null;
    })
    .filter(Boolean)
    .sort((a, b) => ms(b.event?.startTime) - ms(a.event?.startTime));
}

export async function guardarClientesAdicionales({
  venueId,
  eventId,
  clientesAdicionales = [],
  calendarId = "default",
}) {
  if (!venueId || !eventId) return;
  const limpio = clientesAdicionalesDe({ clientesAdicionales });
  await updateDoc(doc(db, "venues", venueId, "calendars", calendarId, "events", eventId), {
    clientesAdicionales: limpio,
    updatedAt: serverTimestamp(),
  });
  return limpio;
}

export async function guardarRolClientePrincipal({ venueId, eventId, rol, calendarId = "default" }) {
  if (!venueId || !eventId) return;
  await updateDoc(doc(db, "venues", venueId, "calendars", calendarId, "events", eventId), {
    clienteRol: trim(rol),
    updatedAt: serverTimestamp(),
  });
}

// ── Escrituras del asistente del directorio (backfill) ───────────────
//
// Crear la ficha y ligar sus eventos es una sola operación: si se parte a
// la mitad queda una ficha sin eventos —o peor, eventos apuntando a una
// ficha que no se creó—. Va en batch.

// Firestore topa el batch en 500 operaciones; se parte antes con holgura.
const OPS_POR_BATCH = 400;

const refEvento = (venueId, calendarId, eventId) =>
  doc(db, "venues", venueId, "calendars", calendarId, "events", eventId);

async function aplicaEnBatches(ops) {
  for (let i = 0; i < ops.length; i += OPS_POR_BATCH) {
    const batch = writeBatch(db);
    ops.slice(i, i + OPS_POR_BATCH).forEach((op) => op(batch));
    await batch.commit(); // eslint-disable-line no-await-in-loop
  }
}

// Igual que `aplicaEnBatches`, pero sin partir un grupo a la mitad.
//
// Un batch sí es atómico; la SECUENCIA de batches no. Al empacar por número
// de operaciones, la ficha de un cliente podía quedar en el batch 1 y el
// enlace de sus eventos en el batch 2: si el segundo fallaba, quedaba una
// ficha huérfana y unos eventos que el asistente ya no vuelve a ofrecer
// —porque los ve ligados— con otros que sí. Empacando por grupo, un fallo
// deja clientes COMPLETOS hechos y el resto intacto, que es un estado del
// que el owner puede salir volviendo a correr el asistente.
async function aplicaEnBatchesPorGrupo(grupos) {
  let pendientes = [];
  const vaciar = async () => {
    if (!pendientes.length) return;
    const batch = writeBatch(db);
    pendientes.forEach((op) => op(batch));
    await batch.commit();
    pendientes = [];
  };
  for (const grupo of grupos) {
    // Un grupo más grande que el batch entero no cabe completo en ninguno;
    // se parte porque es eso o no escribirlo. Son 400 eventos de un mismo
    // cliente: no existe, pero si existiera no puede quedar sin registrarse.
    if (grupo.length > OPS_POR_BATCH) {
      await vaciar(); // eslint-disable-line no-await-in-loop
      await aplicaEnBatches(grupo); // eslint-disable-line no-await-in-loop
      continue;
    }
    if (pendientes.length + grupo.length > OPS_POR_BATCH) {
      await vaciar(); // eslint-disable-line no-await-in-loop
    }
    pendientes.push(...grupo);
  }
  await vaciar();
}

// Una ficha con sus eventos. `rellenarCampos` escribe los datos corregidos
// de vuelta sobre los eventos: si el owner arregló un correo mal capturado,
// el evento también queda bien. Se apaga cuando el contacto del evento era
// de verdad otra persona (la asistente que atendió ese día, por ejemplo).
export async function crearClienteConEventos({
  venueId,
  datos,
  eventIds = [],
  user,
  origen = "asistente",
  calendarId = "default",
  rellenarCampos = true,
}) {
  if (!venueId || !user?.uid) throw new Error("Sin venue o sin usuario");
  const limpio = limpiaDatosCliente(datos);
  if (!tieneIdentidad(limpio)) throw new Error("El cliente necesita al menos un nombre, correo o teléfono.");

  const clienteRef = doc(collection(db, "venues", venueId, "clientes"));
  const ops = [
    (b) =>
      b.set(clienteRef, {
        ...limpio,
        origen,
        archivedAt: null,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      }),
  ];
  const patchBase = rellenarCampos
    ? camposEventoDesdeCliente({ ...limpio, id: clienteRef.id })
    : { clienteId: clienteRef.id };
  eventIds.forEach((eventId) => {
    ops.push((b) =>
      b.update(refEvento(venueId, calendarId, eventId), { ...patchBase, updatedAt: serverTimestamp() })
    );
  });
  // Un solo grupo: la ficha y sus eventos se escriben juntos o no se
  // escriben.
  await aplicaEnBatchesPorGrupo([ops]);
  return clienteRef.id;
}

// Varias fichas de un jalón: los clientes de un solo evento, que son la
// mayoría y que no merecen una pantalla cada uno. Un solo viaje por cada
// 400 operaciones en vez de uno por ficha.
export async function crearClientesEnLote({
  venueId,
  grupos = [],
  user,
  origen = "asistente",
  calendarId = "default",
  rellenarCampos = true,
}) {
  if (!venueId || !user?.uid) throw new Error("Sin venue o sin usuario");
  // Las operaciones se agrupan por ficha, no en una lista plana: la ficha y
  // el enlace de sus eventos viajan SIEMPRE en el mismo batch (ver
  // `aplicaEnBatchesPorGrupo`).
  const porGrupo = [];
  const creados = [];
  grupos.forEach(({ datos, eventIds = [] }) => {
    const limpio = limpiaDatosCliente(datos);
    if (!tieneIdentidad(limpio)) return;
    const clienteRef = doc(collection(db, "venues", venueId, "clientes"));
    const ops = [
      (b) =>
        b.set(clienteRef, {
          ...limpio,
          origen,
          archivedAt: null,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        }),
    ];
    const patchBase = rellenarCampos
      ? camposEventoDesdeCliente({ ...limpio, id: clienteRef.id })
      : { clienteId: clienteRef.id };
    eventIds.forEach((eventId) => {
      ops.push((b) =>
        b.update(refEvento(venueId, calendarId, eventId), { ...patchBase, updatedAt: serverTimestamp() })
      );
    });
    porGrupo.push(ops);
    creados.push({ clienteId: clienteRef.id, nombre: nombreCliente(limpio), eventos: eventIds.length });
  });
  if (!porGrupo.length) return [];
  await aplicaEnBatchesPorGrupo(porGrupo);
  return creados;
}

// Ligar varios eventos a una ficha que ya existe.
export async function ligarEventosACliente({
  venueId,
  cliente,
  eventIds = [],
  calendarId = "default",
  rellenarCampos = false,
}) {
  if (!venueId || !cliente?.id || !eventIds.length) return;
  const patch = rellenarCampos ? camposEventoDesdeCliente(cliente) : { clienteId: cliente.id };
  await aplicaEnBatches(
    eventIds.map((eventId) => (b) =>
      b.update(refEvento(venueId, calendarId, eventId), { ...patch, updatedAt: serverTimestamp() })
    )
  );
}

// "Esto no es un cliente": el evento interno, la prueba, el apartado que
// nunca fue de nadie. Se marca para que el asistente no lo vuelva a
// preguntar; el evento no cambia en nada más.
export async function marcarEventosSinFicha({ venueId, eventIds = [], calendarId = "default" }) {
  if (!venueId || !eventIds.length) return;
  await aplicaEnBatches(
    eventIds.map((eventId) => (b) =>
      b.update(refEvento(venueId, calendarId, eventId), {
        sinFichaCliente: true,
        updatedAt: serverTimestamp(),
      })
    )
  );
}
